/**
 * Structured postal rendering for print documents.
 * Company may come only from the canonical marketplace address.company field.
 */

export type PostalAddressBlock = {
  recipient: string | null;
  company: string | null;
  line1: string | null;
  line2: string | null;
  locality: string | null;
  country: string | null;
};

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return t ? t : null;
}

function firstText(rec: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = text(rec[key]);
    if (value) return value;
  }
  return null;
}

function displayState(state: string | null, country: string | null, countryCode: string | null): string | null {
  if (!state) return null;
  const region = (countryCode || country || "").toUpperCase();
  const isAu = region === "AU" || region === "AUS" || region === "AUSTRALIA" || !region;
  if (!isAu && region && region !== "AUSTRALIA") return state;
  const AU: Record<string, string> = {
    "new south wales": "NSW",
    nsw: "NSW",
    victoria: "VIC",
    vic: "VIC",
    queensland: "QLD",
    qld: "QLD",
    "western australia": "WA",
    wa: "WA",
    "south australia": "SA",
    sa: "SA",
    tasmania: "TAS",
    tas: "TAS",
    "australian capital territory": "ACT",
    act: "ACT",
    "northern territory": "NT",
    nt: "NT",
  };
  return AU[state.toLowerCase()] ?? state;
}

function displayCountry(country: string | null, countryCode: string | null): string | null {
  const raw = country || countryCode;
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === "AU" || upper === "AUS") return "Australia";
  if (upper === "NZ" || upper === "NZL") return "New Zealand";
  if (upper === "US" || upper === "USA") return "United States";
  if (upper === "GB" || upper === "UK" || upper === "GBR") return "United Kingdom";
  return country || countryCode;
}

export function postalAddressFromRecord(addr: Record<string, unknown> | null | undefined): PostalAddressBlock | null {
  if (!addr || typeof addr !== "object") return null;
  const rec = addr;
  const first = text(rec.first_name);
  const last = text(rec.last_name);
  const joined = [first, last].filter(Boolean).join(" ").trim();
  const recipient = firstText(rec, ["name", "full_name", "recipient"]) || (joined || null);
  const company = text(rec.company);
  const line1 = firstText(rec, ["address_line1", "line1", "street_1", "street"]);
  const line2 = firstText(rec, ["address_line2", "line2", "street_2"]);
  const city = firstText(rec, ["city", "suburb"]);
  const countryCode = text(rec.country_code);
  const countryRaw = text(rec.country);
  const state = displayState(firstText(rec, ["state", "region"]), countryRaw, countryCode);
  const postcode = firstText(rec, ["postal_code", "postcode", "zip"]);
  const locality = [city, state, postcode].filter(Boolean).join(" ") || null;
  const country = displayCountry(countryRaw, countryCode);
  if (!recipient && !company && !line1 && !line2 && !locality && !country) return null;
  return { recipient, company, line1, line2, locality, country };
}

export function postalAddressHasContent(block: PostalAddressBlock | null | undefined): boolean {
  if (!block) return false;
  return Boolean(block.recipient || block.company || block.line1 || block.line2 || block.locality || block.country);
}
